import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBars, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { categoriesDB } from "../constants";
import MobileDrawer from './MobileDrawer';

const CategoryMenu = () => {
  const navigate = useNavigate();
  const firstLevelCategories = useMemo(() => Object.keys(categoriesDB || {}), []);

  const [activeDesktopRoot, setActiveDesktopRoot] = useState(null);
  const [activeDesktopSecond, setActiveDesktopSecond] = useState(null);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileLevel, setMobileLevel] = useState(1);
  const [mobileRoot, setMobileRoot] = useState("");
  const [mobileSecond, setMobileSecond] = useState("");

  const goToSearch = (term) => {
    navigate(`/search/${term}`);
    setMobileOpen(false);
    setMobileLevel(1);
    setMobileRoot("");
    setMobileSecond("");
    setActiveDesktopRoot(null);
    setActiveDesktopSecond(null);
  };

  const handleDesktopRootEnter = (root) => {
    setActiveDesktopRoot(root);
    setActiveDesktopSecond(null);
  };

  const handleDesktopLeave = () => {
    setActiveDesktopRoot(null);
    setActiveDesktopSecond(null);
  };

  const openMobileRoot = (root) => {
    setMobileRoot(root);
    setMobileLevel(2);
  };

  const openMobileSecond = (second) => {
    setMobileSecond(second);
    setMobileLevel(3);
  };

  const mobileBack = () => {
    if (mobileLevel === 3) {
      setMobileLevel(2);
      setMobileSecond("");
      return;
    }
    if (mobileLevel === 2) {
      setMobileLevel(1);
      setMobileRoot("");
    }
  };

  return (
    <div className="border-t border-slate-200 bg-slate-50/70">
      <div className="mx-auto max-w-[1400px] px-4 py-2 md:px-6">
        <div className="hidden items-center gap-2 lg:flex" onMouseLeave={handleDesktopLeave}>
          {firstLevelCategories.map((root) => {
            const secondLevel = Object.keys(categoriesDB[root] || {});
            const currentSecond = activeDesktopSecond || secondLevel[0];
            const thirdLevel = categoriesDB[root]?.[currentSecond] || [];

            return (
              <div key={root} className="relative">
                <button
                  type="button"
                  onMouseEnter={() => handleDesktopRootEnter(root)}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                    activeDesktopRoot === root
                      ? "bg-white text-sky-700 shadow-sm"
                      : "text-slate-700 hover:bg-white"
                  }`}
                >
                  {root}
                </button>

                {activeDesktopRoot === root && (
                  <div className="absolute left-0 top-full z-50 grid min-w-[540px] grid-cols-[220px_1fr] gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-xl">
                    <div className="max-h-[320px] overflow-y-auto p-2">
                      {secondLevel.map((second) => (
                        <button
                          key={second}
                          type="button"
                          onMouseEnter={() => setActiveDesktopSecond(second)}
                          onClick={() => goToSearch(second)}
                          className={`flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm ${
                            currentSecond === second
                              ? "bg-sky-50 text-sky-700"
                              : "text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <span>{second}</span>
                          <FaChevronRight className="text-xs opacity-60" />
                        </button>
                      ))}
                    </div>

                    <div className="max-h-[320px] overflow-y-auto rounded-xl border border-slate-100 p-2">
                      {thirdLevel.length > 0 ? (
                        thirdLevel.map((third) => (
                          <button
                            key={third}
                            type="button"
                            onClick={() => goToSearch(third)}
                            className="block w-full rounded-lg px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                          >
                            {third}
                          </button>
                        ))
                      ) : (
                        <p className="px-2 py-2 text-sm text-slate-500">No subcategories</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="lg:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
          >
            <FaBars />
            <span>Categories</span>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <MobileDrawer title="Categories" onClose={() => setMobileOpen(false)}>
          <aside
            className="min-w-0 bg-white [overflow-wrap:anywhere]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {mobileLevel > 1 && (
                  <button
                    type="button"
                    onClick={mobileBack}
                    className="rounded-md p-2 text-slate-600 hover:bg-slate-100"
                    aria-label="Back"
                  >
                    <FaChevronLeft />
                  </button>
                )}
                <h3 className="m-0 text-base font-semibold text-slate-900">
                  {mobileLevel === 1 ? "Browse categories" : mobileLevel === 2 ? mobileRoot : mobileSecond}
                </h3>
              </div>

            </div>

            {mobileLevel === 1 && (
              <div className="grid gap-2">
                {firstLevelCategories.map((root) => (
                  <button
                    key={root}
                    type="button"
                    onClick={() => openMobileRoot(root)}
                    className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <span>{root}</span>
                    <FaChevronRight className="text-xs opacity-60" />
                  </button>
                ))}
              </div>
            )}

            {mobileLevel === 2 && (
              <div className="grid gap-2">
                {Object.keys(categoriesDB[mobileRoot] || {}).map((second) => (
                  <div key={second} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => goToSearch(second)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
                      aria-label={`Search ${second}`}
                    >
                      <FaChevronRight className="text-xs" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openMobileSecond(second)}
                      className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      {second}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {mobileLevel === 3 && (
              <div className="grid gap-2">
                {(categoriesDB[mobileRoot]?.[mobileSecond] || []).map((third) => (
                  <button
                    key={third}
                    type="button"
                    onClick={() => goToSearch(third)}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    {third}
                  </button>
                ))}
              </div>
            )}
          </aside>
        </MobileDrawer>
      )}
    </div>
  );
};

export default CategoryMenu;
